import type { SkillCategory } from '@applicator/shared'
import type { SupabaseClient } from '@supabase/supabase-js'

import { mergeCatalogWithResumeInclusions } from './accountSkills'
import { getErrorMessage } from './errorMessage'

function formatResumeTemplateError(error: unknown): Error {
  const message = getErrorMessage(error, 'Failed to update template flag')
  const code =
    error && typeof error === 'object' && 'code' in error
      ? String((error as { code: unknown }).code)
      : ''

  if (
    code === 'PGRST204' ||
    (message.includes('is_template') && message.includes('schema cache'))
  ) {
    return new Error(
      'Template resumes are not enabled on this database yet. Ask to apply the resumes_is_template migration on Supabase staging.',
    )
  }

  return error instanceof Error ? error : new Error(message)
}

export interface TemplateResumeSummary {
  id: string
  title: string
  updatedAt: string
}

export type SkillsInclusionPreset = 'all_off' | 'all_on' | 'from_template'

export async function fetchTemplateResumes(
  supabase: SupabaseClient,
): Promise<TemplateResumeSummary[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return []

  const { data, error } = await supabase
    .from('resumes')
    .select('id, title, updated_at')
    .eq('user_id', user.id)
    .eq('is_template', true)
    .order('updated_at', { ascending: false })

  if (error) throw formatResumeTemplateError(error)

  return (data ?? []).map((row) => ({
    id: row.id,
    title: row.title,
    updatedAt: row.updated_at,
  }))
}

export async function setResumeTemplateFlag(
  supabase: SupabaseClient,
  resumeId: string,
  isTemplate: boolean,
): Promise<void> {
  const { error } = await supabase
    .from('resumes')
    .update({ is_template: isTemplate })
    .eq('id', resumeId)

  if (error) throw formatResumeTemplateError(error)
}

export async function fetchTemplateSkills(
  supabase: SupabaseClient,
  templateResumeId: string,
): Promise<SkillCategory[] | null> {
  const { data, error } = await supabase
    .from('resume_content')
    .select('content')
    .eq('resume_id', templateResumeId)
    .eq('section_type', 'skills')
    .maybeSingle()

  if (error) throw formatResumeTemplateError(error)
  if (!data?.content) return null

  const raw = data.content
  if (Array.isArray(raw)) return raw as SkillCategory[]

  if (
    raw &&
    typeof raw === 'object' &&
    Array.isArray((raw as { categories?: unknown }).categories)
  ) {
    return (raw as { categories: SkillCategory[] }).categories
  }

  return null
}

export function applySkillsInclusionPreset(
  catalog: SkillCategory[],
  preset: SkillsInclusionPreset,
  templateSkills?: SkillCategory[] | null,
): SkillCategory[] {
  switch (preset) {
    case 'all_off':
      return mergeCatalogWithResumeInclusions(catalog, [], { defaultIncluded: false })
    case 'all_on':
      return mergeCatalogWithResumeInclusions(catalog, [], { defaultIncluded: true })
    case 'from_template':
      return mergeCatalogWithResumeInclusions(catalog, templateSkills ?? [], {
        defaultIncluded: false,
      })
    default:
      return catalog
  }
}
